<script setup>
import i18n from "@/i18n";
import { Toast, toastError } from "@/utils";
import { EventManager } from "@events";
import axios from "axios";
import { computed, ref, watch } from "vue";

const props = defineProps({
  rtcId: {
    type: String,
    required: true,
  },
});

const showModal = ref(false);
const actionEmitter = EventManager.getActionEmitter(props.rtcId);

actionEmitter.on("open-transfer-start-modal", () => {
  showModal.value = true;
  load();
});

const loading = ref(true);
const selectedTab = ref("");
const targetSearch = ref("");
const searchField = ref(null);
const transferTargets = ref([]);

const selectedTarget = ref(null);

const transferTargetTabs = computed(() => {
  // from transfer targets, return distict target.category
  var rest = ["All", ...new Set(transferTargets.value.map((target) => target.category))];
  return rest;
});

const transferTabData = computed(() => {
  var search = targetSearch.value ?? "";

  if (selectedTab.value === "All") {
    return transferTargets.value.filter((target) => target.displayName.toLowerCase().includes(search.toLowerCase()));
  }

  const filtered = transferTargets.value.filter(
    (target) => target.category === selectedTab.value && target.displayName.toLowerCase().includes(search.toLowerCase()),
  );
  return filtered;
});

// when tab is changed, reset search
watch(selectedTab, () => {
  targetSearch.value = "";
  // focus search field
  searchField.value?.focus();
});

async function selectTarget(target) {
  selectedTarget.value = target;
  // scroll to selected item

  if (!target) {
    return;
  }

  const index = transferTabData.value.indexOf(target);
  const item = document.getElementsByClassName("transfer-target-item")[index];
  item.scrollIntoView({ behavior: "smooth", block: "center" });
}

async function transferToTarget(target) {
  console.log("Transferring Call to Target: ", target);
  showModal.value = false;

  if (window.ClientTransport) {
    console.log("Executing Transfer Strategy via ClientTransport");
    await ClientTransport.send("ecvTransferBridge/executeTransferStrategy", { ...target, RtcId: props.rtcId });
    console.log("Transfer Strategy Executed");
    return;
  } else {
    console.warn("ClientTransport not available, falling back to API call");
    toastError("ClientTransport not available");
  }
}

function searchFieldOnDown(event) {
  if (event.key === "Enter") {
    // if item is selected, close modal
    if (selectedTarget.value != null) {
      transferToTarget(selectedTarget.value);
    } else {
      // if no item is selected, select first item
      if (transferTabData.value.length > 0) {
        selectTarget(transferTabData.value[0]);
      }
    }
  }
  // handle up and down arrow keys
  if (event.key === "ArrowDown") {
    // if no item is selected, select first item
    if (selectedTarget.value == null && transferTabData.value.length > 0) {
      selectTarget(transferTabData.value[0]);
    } else {
      // if item is selected, select next item
      const index = transferTabData.value.indexOf(selectedTarget.value);
      if (index < transferTabData.value.length - 1) {
        selectTarget(transferTabData.value[index + 1]);
      }
    }
  }

  if (event.key === "ArrowUp") {
    // if no item is selected, select last item
    if (selectedTarget.value == null && transferTabData.value.length > 0) {
      selectTarget(transferTabData.value[transferTabData.value.length - 1]);
    } else {
      // if item is selected, select previous item
      const index = transferTabData.value.indexOf(selectedTarget.value);
      if (index > 0) {
        selectTarget(transferTabData.value[index - 1]);
      }
    }
  }
}

async function load() {
  loading.value = true;
  try {
    const resp = await axios.get("/api/call/transfer/targets");
    transferTargets.value = resp.data.data;
    // if selectedTab is not in tramsferTargetTabs, and transferTargets is not empty, set selectedTab to first tab
    if (!transferTargetTabs.value.includes(selectedTab.value)) {
      selectedTab.value = transferTargetTabs.value[0];
    }

    // reset search
    selectTarget(null);
    targetSearch.value = "";
  } catch (e) {
    showModal.value = false;
    toastError(e);
  } finally {
    loading.value = false;
  }
}
</script>

<template>
  <v-dialog v-model="showModal" width="500">
    <v-card>
      <v-card-title>
        <v-icon @click="load">mdi-reload</v-icon>
        {{ $t("Transfer") }}
      </v-card-title>
      <v-card-text v-if="!loading">
        <v-row>
          <v-col cols="12">
            <v-tabs v-model="selectedTab">
              <v-tab v-for="tab in transferTargetTabs" :key="tab" :value="tab">{{ $t(tab) }}</v-tab>
            </v-tabs>
          </v-col>
          <v-col cols="12">
            <v-text-field v-model="targetSearch" :label="$t('Search')" clearable autofocus ref="searchField"
              @keydown="searchFieldOnDown" />
            <v-list class="transfer-target-list">
              <v-list-item v-for="item in transferTabData"
                :key="item.displayName.category + item.displayName + item.targetName" @click="selectTarget(item)"
                :class="'transfer-target-item' + (item === selectedTarget ? ' selected' : '')">
                <!-- item.icon is mdi icon class -->
                <template v-slot:prepend>
                  <v-icon :icon="item.icon"></v-icon>
                </template>

                <v-list-item-title>{{ item.displayName }}</v-list-item-title>
                <v-list-item-subtitle>
                  {{ item.category }} {{ item.targetName != item.displayName ? `(${item.targetName})` : "" }}
                </v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-text v-else>
        <v-row>
          <v-col class="text-center">
            <v-progress-circular indeterminate color="primary" />
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-btn color="secondary" @click="showModal = false"> {{ $t("Cancel") }} </v-btn>
        <v-btn :disabled="loading || selectedTarget == null" color="primary" @click="transferToTarget(selectedTarget)">
          {{ $t("Transfer") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.transfer-target-list {
  border: 1px solid #e0e0e063;
  overflow: hidden;
  padding: 0;
  margin: 0;
  max-height: 250px;
  overflow: auto;
}

.transfer-target-item {
  cursor: pointer;
  border-bottom: 1px solid #e0e0e063;
}

/** last  item does not need border */
.transfer-target-item:last-child {
  border-bottom: none;
}

.selected {
  background-color: #e0e0e063;
}
</style>
